import { useMemo } from 'react'
import {
  ActivityUserRole,
  Permission,
  UserRoleType
} from '../types/activity_user'

export const usePermissions = (user) => {
  const permissions = useMemo(() => {
    if (!user) {
      return {
        canRead: () => false,
        canCreate: () => false,
        canUpdate: () => false,
        canDelete: () => false,
        hasPermission: () => false,
        isActivityUser: false,
        isManagedUser: false,
        isServiceProvider: false,
        isProcessor: false,
        canCreateUsers: false,
        roleName: null,
        roleDisplayName: null,
        capabilities: getDefaultCapabilities()
      }
    }

    const isManagedUser = Boolean(user?.is_managed || user?.is_managed_user || user?.user_context === 'managed')

    if (isManagedUser) {
      const assignedRoles = user?.managed_permissions?.assigned_roles || user?.roles || []
      const modulePermissions = assignedRoles.reduce((acc, role) => {
        const rolePermissions = Array.isArray(role?.permissions) ? role.permissions : []
        rolePermissions.forEach((permission) => {
          const moduleName = permission?.module
          const action = permission?.action
          if (!moduleName || !action) return
          if (!acc[moduleName]) acc[moduleName] = new Set()
          acc[moduleName].add(action)
        })
        return acc
      }, {})

      const hasPermission = (resource, permission) => {
        const candidates = [resource]
        if (resource?.endsWith('s')) candidates.push(resource.slice(0, -1))
        else candidates.push(`${resource}s`)

        for (const moduleName of candidates) {
          const actions = modulePermissions[moduleName]
          if (actions && (actions.has(permission) || actions.has(Permission.FULL_ACCESS))) {
            return true
          }
        }
        return false
      }

      const canRead = (resource) => hasPermission(resource, Permission.READ)
      const canCreate = (resource) => hasPermission(resource, Permission.CREATE)
      const canUpdate = (resource) => hasPermission(resource, Permission.UPDATE)
      const canDelete = (resource) => hasPermission(resource, Permission.DELETE)

      const capabilities = {
        canCreateActivities: canCreate('activities') || canCreate('harvest_requests'),
        canRegisterUnregisterFarmers: canUpdate('farmers') || canUpdate('farms') || canCreate('farmers'),
        canViewActivities: canRead('activities') || canRead('harvest_requests'),
        canAddFarmers: canCreate('farmers'),
        canDeleteFarmers: canDelete('farmers'),
        canCreateUsers: canCreate('admin'),
        canCreateProgramOfficers: false,
        canCreateFieldOfficers: false,
        canMapFarms: canCreate('farms') || canUpdate('farms'),
        canUploadPhotos: canCreate('farms') || canUpdate('farms'),
        canAccessWeb: true,
        canAccessMobile: true,
        canManageProgram: canRead('admin') || canUpdate('admin'),
        canAddFarms: canCreate('farms'),
        canSendSMS: canCreate('ha_campaigns') || canUpdate('ha_campaigns'),
        canUpdateFarmers: canUpdate('farmers'),
        canViewFarmers: canRead('farmers'),
        canViewFarms: canRead('farmers'),
        canViewGroups: canRead('ha_groups'),
        canViewProcessors: canRead('service_providers'),
        canViewServices: canRead('service_providers'),
        canViewRequest: canRead('harvest_requests'),
        CanViewAppointments: false,
        canViewReports: canRead('admin') || canRead('ha_campaigns'),
        canViewCampaigns: canRead('ha_campaigns'),
        canViewServiceOrders: canRead('service_orders'),
        CanViewDashboard: true
      }

      return {
        canRead,
        canCreate,
        canUpdate,
        canDelete,
        hasPermission,
        isActivityUser: false,
        isManagedUser: true,
        isServiceProvider: false,
        isProcessor: false,
        canCreateUsers: capabilities.canCreateUsers,
        roleName: 'managed_user',
        roleDisplayName: 'Managed User',
        capabilities
      }
    }

    // Check if user is service provider or processor (full access)
    const isServiceProvider = user.user_type === UserRoleType.service_provider
    const isProcessor = user.user_type === UserRoleType.processor || user.user_type === 'miller'
    const isActivityUser = user.is_activity_user || user.activity_role || user.user_type === 'activity_user'

    // // Debug logging
    // console.log('=== PERMISSIONS DEBUG ===')
    // console.log('User object:', user)
    // console.log('user_type:', user.user_type)
    // console.log('is_activity_user:', user.is_activity_user)
    // console.log('activity_role:', user.activity_role)
    // console.log('isServiceProvider:', isServiceProvider)
    // console.log('isProcessor:', isProcessor)
    // console.log('isActivityUser:', isActivityUser)
    // console.log('========================')

    // For service providers and processors, allow all permissions
    if (isServiceProvider || isProcessor) {
      return {
        canRead: () => true,
        canCreate: () => true,
        canUpdate: () => true,
        canDelete: () => true,
        hasPermission: () => true,
        isActivityUser: false,
        isManagedUser: false,
        isServiceProvider,
        isProcessor,
        canCreateUsers: true,
        roleName: user.user_type,
        roleDisplayName: user.user_type === UserRoleType.service_provider ? 'Service Provider' : 'Processor',
        capabilities: getServiceProviderCapabilities()
      }
    }

    // For activity users, check their specific permissions
    if ((isActivityUser || user.activity_role) && user.activity_role) {
      const activityPermissions = user.activity_role.permissions
      const roleName = user.activity_role.role_name

      // Enhance permissions with derived capabilities based on role
      const enhancedPermissions = {
        ...activityPermissions,
        program_management: activityPermissions.activities || [],
        field_activities: activityPermissions.farms || [],
        web_access: roleName !== ActivityUserRole.FIELD_OFFICER,
        mobile_access: true
      }

      const hasPermission = (resource, permission) => {
        const resourcePermissions = enhancedPermissions[resource]
        return Array.isArray(resourcePermissions) ? resourcePermissions.includes(permission) : false
      }

      return {
        canRead: (resource) => hasPermission(resource, Permission.READ),
        canCreate: (resource) => hasPermission(resource, Permission.CREATE),
        canUpdate: (resource) => hasPermission(resource, Permission.UPDATE),
        canDelete: (resource) => hasPermission(resource, Permission.DELETE),
        hasPermission,
        isActivityUser: true,
        isManagedUser: false,
        isServiceProvider: false,
        isProcessor: false,
        canCreateUsers: user.activity_role.can_create_users,
        roleName: user.activity_role.role_name,
        roleDisplayName: user.activity_role.display_name,
        capabilities: getRoleCapabilities(roleName, enhancedPermissions)
      }
    }

    // Default case - no permissions
    return {
      canRead: () => false,
      canCreate: () => false,
      canUpdate: () => false,
      canDelete: () => false,
      hasPermission: () => false,
      isActivityUser: false,
      isManagedUser: false,
      isServiceProvider: false,
      isProcessor: false,
      canCreateUsers: false,
      roleName: user.user_type,
      roleDisplayName: user.user_type,
      capabilities: getDefaultCapabilities()
    }
  }, [user])

  return permissions
}

// Helper functions to get role capabilities based on requirements
function getDefaultCapabilities() {
  return {
    canCreateActivities: true,
    canRegisterUnregisterFarmers: true,
    canViewActivities: true,
    canAddFarmers: true,
    canDeleteFarmers: true,
    canCreateUsers: true,
    canCreateProgramOfficers: true,
    canCreateFieldOfficers: true,
    canMapFarms: true,
    canUploadPhotos: true,
    canAccessWeb: true,
    canAccessMobile: true,
    canManageProgram: true,
    canAddFarms: true,
    canSendSMS: true
  }
}

function getServiceProviderCapabilities() {
  return {
    canCreateActivities: true,
    canRegisterUnregisterFarmers: true,
    canViewActivities: true,
    canAddFarmers: true,
    canDeleteFarmers: true,
    canCreateUsers: true,
    canCreateProgramOfficers: true,
    canCreateFieldOfficers: true,
    canMapFarms: true,
    canUploadPhotos: true,
    canAccessWeb: true,
    canAccessMobile: true,
    canManageProgram: true,
    canAddFarms: true,
    canSendSMS: true,
    canUpdateFarmers: true
  }
}

function getRoleCapabilities(role, permissions) {
  // Base capabilities from API permissions
  const canReadActivities = permissions.activities?.includes(Permission.READ) || false
  const canCreateActivities = permissions.activities?.includes(Permission.CREATE) || false
  const canUpdateActivities = permissions.activities?.includes(Permission.UPDATE) || false
  const canDeleteActivities = permissions.activities?.includes(Permission.DELETE) || false

  const canReadFarmers = permissions.farmers?.includes(Permission.READ) || false
  const canCreateFarmers = permissions.farmers?.includes(Permission.CREATE) || false
  const canUpdateFarmers = permissions.farmers?.includes(Permission.UPDATE) || false
  const canDeleteFarmers = permissions.farmers?.includes(Permission.DELETE) || false

  const canReadFarms = permissions.farms?.includes(Permission.READ) || false
  const canCreateFarms = permissions.farms?.includes(Permission.CREATE) || false
  const canUpdateFarms = permissions.farms?.includes(Permission.UPDATE) || false
  const canDeleteFarms = permissions.farms?.includes(Permission.DELETE) || false

  switch (role) {
    case ActivityUserRole.ADMIN:
      return {
        canCreateActivities: true,
        canRegisterUnregisterFarmers: true,
        canViewActivities: true,
        canAddFarmers: true,
        canDeleteFarmers: true,
        canCreateUsers: true,
        canCreateProgramOfficers: true,
        canCreateFieldOfficers: true,
        canMapFarms: true,
        canUploadPhotos: true,
        canAccessWeb: true,
        canAccessMobile: true,
        canManageProgram: true,
        canAddFarms: true,
        canUpdateFarmers: false,

      }

    case ActivityUserRole.PROGRAM_MANAGER:
      return {
        canCreateActivities: canCreateActivities,
        canRegisterUnregisterFarmers: canUpdateFarms || canCreateFarms,
        canViewActivities: canReadActivities,
        canAddFarmers: canCreateFarmers,
        canDeleteFarmers: canDeleteFarmers,
        canCreateUsers: true,
        canCreateProgramOfficers: true,
        canCreateFieldOfficers: true,
        canMapFarms: false,
        canUploadPhotos: false,
        canAccessWeb: true,
        canAccessMobile: true,
        canManageProgram: true,
        canAddFarms: true,

        canViewFarmers: canCreateFarmers,
        canViewFarms: canCreateFarms,
        canViewGroups: false,
        canViewProcessors: false,
        canViewServices: false,
        canViewRequest: false,
        CanViewAppointments: false,
        canViewReports: false,
        canViewCampaigns: false,
        canViewServiceOrders: false,
        CanViewDashboard: true,
        canUpdateFarmers: false,

      }

    case ActivityUserRole.PROGRAM_OFFICER:
      return {
        canCreateActivities: canCreateActivities,
        canRegisterUnregisterFarmers: canUpdateFarms || canCreateFarms,
        canViewActivities: canReadActivities,
        canAddFarmers: canCreateFarmers,
        canDeleteFarmers: canDeleteFarmers,
        canCreateUsers: false,
        canCreateProgramOfficers: false,
        canCreateFieldOfficers: false,
        canMapFarms: false,
        canUploadPhotos: false,
        canAccessWeb: true,
        canAccessMobile: true,
        canManageProgram: false,
        canAddFarms: true,

        canViewFarmers: canCreateFarmers,
        canViewFarms: canCreateFarms,
        canViewGroups: false,
        canViewProcessors: false,
        canViewServices: false,
        canViewRequest: false,
        CanViewAppointments: false,
        canViewReports: false,
        canViewCampaigns: false,
        canViewServiceOrders: false,
        CanViewDashboard: true,
        canUpdateFarmers: false,
      }

    case ActivityUserRole.FIELD_OFFICER:
      return {
        canCreateActivities: false,
        canRegisterUnregisterFarmers: false,
        canViewActivities: canReadActivities,
        // canAddFarmers: canCreateFarmers,
        // canDeleteFarmers: false, // Cannot delete farmers added by other users
        // canCreateUsers: false,
        canAddFarmers: true,
        canDeleteFarmers: true,
        canCreateUsers: true,
        canViewGroups: false,
        canViewProcessors: false,
        canCreateProgramOfficers: false,
        canCreateFieldOfficers: false,
        canMapFarms: canCreateFarms || canUpdateFarms,
        canUploadPhotos: canCreateFarms || canUpdateFarms,
        canAccessWeb: false, // Mobile app only
        canAccessMobile: true,
        canManageProgram: false,
        canAddFarms: true,

        canViewFarmers: true,
        canViewFarms: true,
        canViewServices: false,
        canViewRequest: false,
        CanViewAppointments: false,
        canViewReports: false,
        canViewCampaigns: false,
        canViewServiceOrders: false,
        CanViewDashboard: true,
        canUpdateFarmers: false,
      }

    default:
      return getDefaultCapabilities()
  }
}
